import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useMutation } from 'convex/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { keyToDate, logicalDayStr } from '@/lib/day'
import { dueOnDate } from '@/lib/schedule'
import { undoToast } from '@/lib/undo-toast'

const DAY_MS = 86_400_000

// Yesterday's logical day (honours the day-start hour, like "done today").
export const yesterdayKey = () => logicalDayStr(Date.now() - DAY_MS)

export const doneOnDay = (completions: number[], day: string) =>
  completions.some((ms) => logicalDayStr(ms) === day)

// Whether a habit with this cadence was due on `day`.
export const dueOn = dueOnDate

export const yesterdayDate = () => keyToDate(yesterdayKey())

// Late-night habits often get checked off the next morning: log (or unlog)
// yesterday's completion. Fills that day's square and counts toward streaks.
export function useMarkYesterday() {
  const { t } = useTranslation()
  const mark = useSetDay()
  return async (taskId: Id<'nodes'>, title: string, done = true) => {
    const ok = await mark(taskId, yesterdayKey(), done)
    if (!ok) {
      toast.error(t('habitYesterday.error'))
      return false
    }
    if (done)
      undoToast(
        t('habitYesterday.marked', { title }),
        () => void mark(taskId, yesterdayKey(), false),
      )
    else toast.success(t('habitYesterday.unmarked'))
    return true
  }
}

// Any recent day (the server allows the last 7): a missed dot's "Mark done".
export function useMarkDay() {
  const { t, i18n } = useTranslation()
  const mark = useSetDay()
  return async (taskId: Id<'nodes'>, title: string, date: string) => {
    if (!(await mark(taskId, date, true))) {
      toast.error(t('habitDay.error'))
      return false
    }
    const day = keyToDate(date).toLocaleDateString(i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    undoToast(t('habitDay.marked', { title, day }), () => void mark(taskId, date, false))
    return true
  }
}

function useSetDay() {
  const set = useMutation(api.nodes.setCompletionForDate).withOptimisticUpdate(
    (store, { taskId, date, done }) => {
      const rows = store.getQuery(api.nodes.getRecurringCompletionDates, {})
      if (rows)
        store.setQuery(
          api.nodes.getRecurringCompletionDates,
          {},
          rows.map((r) =>
            r.taskId === taskId
              ? {
                  ...r,
                  dates: done
                    ? r.dates.includes(date)
                      ? r.dates
                      : [...r.dates, date]
                    : r.dates.filter((d) => d !== date),
                }
              : r,
          ),
        )
      const habits = store.getQuery(api.nodes.getHabits, {})
      if (habits)
        store.setQuery(
          api.nodes.getHabits,
          {},
          habits.map((h) => {
            if (h._id !== taskId || doneOnDay(h.completions, date) === done) return h
            const completions = done
              ? [...h.completions, keyToDate(date).getTime() + 12 * 3_600_000].sort((a, b) => a - b)
              : h.completions.filter((ms) => logicalDayStr(ms) !== date)
            return { ...h, completions }
          }),
        )
    },
  )
  return (taskId: Id<'nodes'>, date: string, done: boolean) =>
    set({ taskId, date, tzOffset: new Date().getTimezoneOffset(), done }).then(
      () => true,
      () => false,
    )
}
